/** The calculator's editable state, plus the derivation from state → daily targets. */

import {
  calcEnergy,
  calcTargetCalories,
  defaultFiber,
  defaultWeeklyRangeForGoal,
  recommendMacros,
  mapStepsToLevel,
  missingBmrInputs,
  toGoalValue,
  toWeeklyRangeValue,
  CARB_RANGE,
  FAT_RANGE,
  PROTEIN_RANGE,
  type BmrFormula,
  type EnergyBreakdown,
  type Gender,
  type GoalValue,
  type MacroRecommendation,
} from "../../../../utils/nutritionCalculator";
import type { MacroSet } from "../model/nutritionV2Model";

export type CalculatorState = {
  gender: Gender;
  age: number | "";
  weight: number | "";
  height: number | "";
  bodyFat: number | "";
  formula: BmrFormula;
  steps: number;
  workouts: number;
  workoutDuration: number;
  /** `enums/GoalTypes` value — the same goal the weight page uses */
  goal: GoalValue;
  /** `enums/WeeklyRanges` value — the same weekly range the weight page uses */
  weeklyRange: string;
  /** set when the trainer types a target by hand; null = follow the calculation */
  customTarget: number | null;
  proteinPerKg: number;
  fatPerKg: number;
  carbsPerKg: number;
  carbsOverride: boolean;
  fiber: number;
  /**
   * The trainer created this menu WITHOUT the calculator: there are no calculated targets, so the
   * menu's own foods are the recommendation and the targets follow them as it is built.
   *
   * It rides along in the calculator blob rather than on the menu because that blob is opaque to
   * the server — stored and returned verbatim — so remembering the choice costs no column, no
   * migration and no DTO. Absent on every menu written before this existed, which is exactly
   * right: those menus have calculated targets and must not start tracking their meals.
   */
  skipped?: boolean;
};

/**
 * Whether a stored calculator has anything of the trainee in it.
 *
 * A menu saved before the calculator was persisted — or one copied from a template, or duplicated
 * — comes back with nothing here, and the panel then opened completely blank while the trainee's
 * age, weight and height were sitting in their profile the whole time. This is what says "there is
 * nothing to preserve, prefill it".
 */
export const hasClientData = (state: Pick<CalculatorState, "age" | "weight" | "height">): boolean =>
  Boolean(Number(state.weight) || Number(state.age) || Number(state.height));

/**
 * Whether the calculator has everything its formula needs — i.e. whether its numbers mean
 * anything at all.
 *
 * NOT `energy.tdee > 0`, which looks like the same question and is not: NEAT and EAT fall back to
 * a 70kg body, so an untouched calculator still adds up to a confident-looking ~700 kcal built out
 * of nothing but defaults. Missing inputs are the only honest signal.
 */
export const isCalculable = (state: CalculatorState): boolean =>
  missingBmrInputs({
    formula: state.formula,
    age: Number(state.age) || 0,
    weight: Number(state.weight) || 0,
    height: Number(state.height) || 0,
    bodyFat: Number(state.bodyFat) || undefined,
  }).length === 0;

export const defaultCalculatorState = (): CalculatorState => ({
  gender: "male",
  age: "",
  weight: "",
  height: "",
  bodyFat: "",
  formula: "mifflin",
  steps: 8000,
  workouts: 5,
  workoutDuration: 60,
  goal: "MAINTENANCE",
  weeklyRange: "MAINTENANCE",
  customTarget: null,
  proteinPerKg: PROTEIN_RANGE.default,
  fatPerKg: FAT_RANGE.default,
  carbsPerKg: CARB_RANGE.default,
  carbsOverride: false,
  fiber: defaultFiber("male"),
});

/**
 * Prefill from `NutritionCalculatorData` alone. `buildTraineePrefill` (traineePrefill.ts)
 * is the fuller path — it also folds in the trainee's target steps and active program.
 */
export const calculatorStateFromTrainee = (data: {
  gender?: string;
  age?: number;
  height?: number;
  lastWeight?: number;
  numOfDaysTraining?: number;
  weeklyAverageSteps?: number;
  goal?: string;
  weeklyRange?: string;
}): CalculatorState => {
  const base = defaultCalculatorState();
  const gender: Gender = String(data.gender || "").toUpperCase() === "FEMALE" ? "female" : "male";
  const goal = toGoalValue(data.goal);
  return {
    ...base,
    gender,
    age: data.age || "",
    height: data.height || "",
    weight: data.lastWeight || "",
    steps: mapStepsToLevel(data.weeklyAverageSteps),
    workouts: data.numOfDaysTraining ?? base.workouts,
    goal,
    weeklyRange: toWeeklyRangeValue(data.weeklyRange, goal),
    fiber: defaultFiber(gender),
  };
};

/** Changing the goal re-points the weekly range at that goal's default. */
export const withGoal = (state: CalculatorState, goal: GoalValue): CalculatorState => ({
  ...state,
  goal,
  weeklyRange: defaultWeeklyRangeForGoal(goal),
  customTarget: null,
});

export type DerivedTargets = {
  energy: EnergyBreakdown;
  /** the calculated target, before any manual override */
  calculatedTarget: number;
  /** what the menu actually aims at */
  target: number;
  macros: MacroRecommendation;
  targets: MacroSet;
};

export const deriveTargets = (state: CalculatorState): DerivedTargets => {
  const weight = Number(state.weight) || 0;
  const energy = calcEnergy({
    gender: state.gender,
    age: Number(state.age) || 0,
    weight,
    height: Number(state.height) || 0,
    bodyFat: Number(state.bodyFat) || undefined,
    formula: state.formula,
    steps: state.steps,
    workouts: state.workouts,
    workoutDuration: state.workoutDuration,
  });

  const calculatedTarget = calcTargetCalories(energy.tdee, state.weeklyRange);
  const target = state.customTarget ?? calculatedTarget;

  const macros = recommendMacros({
    targetCalories: target,
    weight,
    proteinPerKg: state.proteinPerKg,
    fatPerKg: state.fatPerKg,
    carbsPerKg: state.carbsPerKg,
    carbsOverride: state.carbsOverride,
  });

  return {
    energy,
    calculatedTarget,
    target,
    macros,
    targets: {
      calories: target,
      protein: macros.proteinG,
      carbs: macros.carbsG,
      fats: macros.fatG,
      fiber: state.fiber,
    },
  };
};

export const isCalculatorReady = (state: CalculatorState): boolean =>
  Boolean(Number(state.weight)) && deriveTargets(state).energy.tdee > 0;
